import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import '@/App.css';

// Context
import { AppProvider, useApp } from './context/AppContext';

// Components
import Navbar from './components/Navbar';
import AudioPlayer from './components/AudioPlayer';

// Pages
import HomePage from './pages/HomePage';
import BeatsPage from './pages/BeatsPage';
import BeatDetailPage from './pages/BeatDetailPage';
import ServicesPage from './pages/ServicesPage';
import CartPage from './pages/CartPage';
import ETransferPage from './pages/ETransferPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import DashboardPage from './pages/DashboardPage';
import CourseContentPage from './pages/CourseContentPage';
import AdminPage from './pages/AdminPage';
import AuthCallback from './pages/AuthCallback';
import StudioBookingPage from './pages/StudioBookingPage';
import MaintenancePage from './pages/MaintenancePage';
import SectionUnavailablePage from './pages/SectionUnavailablePage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Wrapper for BeatDetailPage to pass useParams
const BeatDetailWrapper = () => {
  return <BeatDetailPage />;
};

function AppContent() {
  const { user, setUser, sectionVisibility } = useApp();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);
  const [authComplete, setAuthComplete] = useState(false);
  const location = useLocation();
  
  // Check if we're in auth callback
  const isAuthCallback = location.hash.includes('session_id=') && location.pathname === '/dashboard';

  useEffect(() => {
    checkMaintenance();
  }, []);

  const checkMaintenance = async () => {
    try {
      const response = await axios.get(`${API}/settings/maintenance`);
      setMaintenanceMode(response.data.enabled);
    } catch (e) {
      console.error(e);
    } finally {
      setCheckingMaintenance(false);
    }
  };

  const handleAuthComplete = (authUser) => {
    setAuthComplete(true);
    if (!authUser?.is_admin && authUser) {
      // Non-admin tried to login during maintenance - log them out
      setUser(null);
    }
  };

  // If in auth callback, always show the dashboard to process auth FIRST
  if (isAuthCallback) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <DashboardPage onAuthComplete={handleAuthComplete} />
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(212,175,55,0.3)' }
          }} 
        />
      </div>
    );
  }

  // Show loading while checking maintenance
  if (checkingMaintenance) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show maintenance page for non-admins
  if (maintenanceMode && !user?.is_admin) {
    return <MaintenancePage />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white bg-23-pattern">
      {/* Falling 23 Watermarks */}
      <div className="bg-23-watermark top-left">23</div>
      <div className="bg-23-watermark bottom-right">23</div>
      <div className="bg-23-watermark center">23</div>
      <div className="bg-23-falling">23</div>
      <div className="bg-23-falling">23</div>
      <div className="bg-23-falling">23</div>
      <div className="bg-23-falling">23</div>
      <div className="bg-23-falling">23</div>
      
      {maintenanceMode && user?.is_admin && (
        <div className="bg-yellow-500 text-black text-center py-2 text-sm font-medium relative z-50">
          ⚠️ Site is in maintenance mode - Only you can see it. Visitors see maintenance page.
        </div>
      )}
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/beats" element={sectionVisibility?.beats ? <BeatsPage /> : <SectionUnavailablePage section="Beats" />} />
        <Route path="/beats/:beat_id" element={sectionVisibility?.beats ? <BeatDetailWrapper /> : <SectionUnavailablePage section="Beats" />} />
        <Route path="/services" element={sectionVisibility?.services ? <ServicesPage /> : <SectionUnavailablePage section="Services" />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/payment/etransfer" element={<ETransferPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/course" element={sectionVisibility?.courses ? <CourseContentPage /> : <SectionUnavailablePage section="Courses" />} />
        <Route path="/studio" element={sectionVisibility?.studio ? <StudioBookingPage /> : <SectionUnavailablePage section="Studio Booking" />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <AudioPlayer />
    </div>
  );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppProvider>
      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            color: '#fff'
          }
        }}
      />
      <AppRouter />
    </AppProvider>
  );
}

export default App;
